'use client'

import * as React from 'react'
import { DataTable } from '@/components/data-table/data-table'
import { useDataTable } from '@/hooks/use-data-table'
import { DataTableSortList } from '@/components/data-table/data-table-sort-list'

import { useTaxExemptionsTableColumns } from './tax-exemptions-table-columns'
import { TaxExemptionsTableActions } from './tax-exemptions-table-actions'
import { TaxExemptionsTableActionBar } from './tax-exemptions-table-action-bar'
import { TaxExemptionsTableSkeleton } from './TaxExemptionsTableSkeleton'
import { MainTableState } from '@/lib/utils/table-state-utils'
import type { TableConfig } from '@/types/data-table'
import { DEFAULT_SKELETON_ROWS } from '@/config/data-table'
import { TaxExemptionItem } from '@/lib/api/taxExemptions'
import { BulkDeleteTaxExemptionsModal } from './bulk-delete-tax-exemptions-modal'

interface TaxExemptionsTableProps {
	items: TaxExemptionItem[]
	total: number
	loading: boolean
	onTableStateChange: (state: MainTableState) => void
	onRefetch: () => void
	config?: TableConfig
}

export function TaxExemptionsTable({
	items,
	total,
	loading,
	onTableStateChange,
	onRefetch,
	config = {},
}: TaxExemptionsTableProps) {
	const [deleteId, setDeleteId] = React.useState<string | undefined>(undefined)

	const columns = useTaxExemptionsTableColumns({
		onDeleteTaxExemption: (id: string) => setDeleteId(id),
	})

	const { table } = useDataTable({
		data: items,
		columns,
		total,
		config,
		onTableStateChange,
		getRowId: (row: TaxExemptionItem) => row.id,
	})

	return (
		<DataTable
			table={table}
			actionBar={<TaxExemptionsTableActionBar table={table} />}
			isLoading={loading}
			skeletonRows={<TaxExemptionsTableSkeleton rows={config.skeletonRows || DEFAULT_SKELETON_ROWS} />}
		>
			<TaxExemptionsTableActions table={table} onRefresh={onRefetch} isLoading={loading} />
			<DataTableSortList table={table} align="end" />
			{deleteId && (
				<BulkDeleteTaxExemptionsModal
					taxExemptionIds={[deleteId]}
					onClose={() => setDeleteId(undefined)}
				/>
			)}
		</DataTable>
	)
}
